/* eslint-disable hapi/hapi-scope-start */

const Constant = require('lodash/constant');
const Unary = require('lodash/unary');
const First = require('lodash/first');
const IsEmpty = require('lodash/isEmpty');
const IsPromise = require('is-promise');
const Memoize = require('lodash/memoize');
const { normalize, schema: S } = require('normalizr');
const { safeWarning, safeInvariant } = require('assertions-simplified');
const { MESSAGES_PAGE_SIZE } = require('utils/constants');
const {
    recordTwilioRecoveryEvent,
    runTwilioMessageLoadSimulation
} = require('utils/twilio-recovery-debug');

const { Client } = require('@twilio/conversations');
const { TransportUnavailableError } = require('twilsock');

module.exports = (context) => {

    const actions = context.actions;
    const selectors = context.selectors.all;
    const redux = context.redux.hooks;
    // The Nearpeer API placeholder is populated after this factory returns.
    // Resolve its authentication member when refreshing, not during startup.
    const nearpeerApi = context.api.nearpeer;
    const configuration = context.configuration.twilioApi;
    const MAX_RECONNECT_DELAY_MS = 30000;
    const BASE_RECONNECT_DELAY_MS = 1000;
    const RECOVERY_TIMEOUT_MS = 15000;
    const SEND_TIMEOUT_MS = 30000;
    const SEND_CONVERSATION_TIMEOUT_MS = 10000;
    const SHUTDOWN_TIMEOUT_MS = 5000;
    const IDLE_CONNECTION_MS = 60000;

    const getIsAuthenticated = () => {

        return selectors.getIsAuthenticated(redux.getState());
    };

    const getTwilioToken = () => {

        return selectors.getTwilioToken(redux.getState());
    };

    const isFatalTwilioAuthError = (err) => {

        const code = err?.code ?? err?.errorCode;
        const status = err?.status ?? err?.httpStatusCode;
        const message = String(err?.message || '');

        return status === 401 && (code === 20103 || message.includes('issuer/subject'));
    };

    const yellTwilioFatalAuthError = (err, { phase } = {}) => {

        // Big, loud, obvious in console
        // eslint-disable-next-line no-console
        console.error(
            'FATAL TWILIO AUTH ERROR (will not self-heal)\n' +
            `Phase: ${phase || 'unknown'}\n` +
            'Likely cause: token minted with wrong Account SID / API Key (issuer/subject mismatch), or wrong env creds.\n' +
            `Code: ${err?.code ?? err?.errorCode ?? 'unknown'} | HTTP: ${err?.status ?? err?.httpStatusCode ?? 'unknown'}\n` +
            `Message: ${String(err?.message || '')}\n`,
            err
        );
    };

    // cache-simplified returns rather than throws errors. We want them thrown!
    const makeCacheWithErrors = (...args) => {

        return Object.entries(...args).reduce((collect, [key, fn]) => {

            const resolver = (arg) => arg?.channelSid || arg;
            const memoized = Memoize(async (...fnArgs) => {

                try {
                    const result = await fn(...fnArgs);

                    if (result instanceof Error) {
                        throw result;
                    }

                    return result;
                }
                catch (err) {
                    memoized.cache.delete(resolver(...fnArgs));
                    throw err;
                }
            }, resolver);

            return { ...collect, [key]: memoized };
        }, {});
    };

    let cache;
    let messageEntities;
    let messagesPaginator_current;
    let messagesPaginator_next;

    let twilioClient = null;
    let twilioClientListenersAttached = false;
    let didForceRefreshAfterFatalAuth = false;
    let reconnectTimeout = null;
    let reconnectPromise = null;
    let cancelReconnect = null;
    let reconnectAttempts = 0;
    let clientGeneration = 0;
    let cacheSessionToken;
    let isShutdown = false;
    let messageListenerClient = null;
    let connectionCheckPromise = null;
    let connectionCheckVersion = 0;
    let lastTransportActivityAt = 0;

    const assertActiveGeneration = (generation, sessionToken) => {

        if (generation !== clientGeneration || isShutdown || !getIsAuthenticated()
            || (sessionToken !== undefined && sessionToken !== selectors.getApiToken?.(redux.getState()))) {
            const error = new Error('Twilio operation belongs to an inactive session');
            error.code = 'TWILIO_OPERATION_SUPERSEDED';
            throw error;
        }
    };

    const withActiveGeneration = (func) => async (...args) => {

        const generation = clientGeneration;
        const sessionToken = selectors.getApiToken?.(redux.getState());
        assertActiveGeneration(generation, sessionToken);
        const result = await func(...args);
        assertActiveGeneration(generation, sessionToken);
        return result;
    };

    const clearReconnectTimeout = () => {

        if (reconnectTimeout) {
            clearTimeout(reconnectTimeout);
            reconnectTimeout = null;
        }
    };

    const resetReconnectState = () => {

        clearReconnectTimeout();
        reconnectAttempts = 0;
    };

    const supersedeReconnect = () => {

        connectionCheckPromise = null;
        connectionCheckVersion += 1;
        cancelReconnect?.();
        cancelReconnect = null;
        reconnectPromise = null;
        resetReconnectState();
    };

    const getReconnectDelay = () => {

        return Math.min(
            MAX_RECONNECT_DELAY_MS,
            BASE_RECONNECT_DELAY_MS * (2 ** Math.max(reconnectAttempts - 1, 0))
        );
    };

    const fetchFreshToken = async () => {

        const generation = clientGeneration;
        const sessionToken = selectors.getApiToken?.(redux.getState());
        const { token } = await nearpeerApi.authentication.updateTwilioToken();

        assertActiveGeneration(generation, sessionToken);

        if (typeof token !== 'string' || !token.trim()) {
            throw new Error('Twilio token refresh returned empty token');
        }

        redux.dispatch(actions.auth.updateTwilioToken.success({
            result: { token }
        }));

        return token;
    };

    const attachClientCoreListeners = (client) => {

        if (twilioClientListenersAttached) {
            return;
        }

        // Token refresh listeners (Conversations-native)
        const refreshToken = async () => {

            const newToken = await fetchFreshToken();

            if (client === twilioClient) {
                await client.updateToken(newToken);
            }
        };

        client.on('tokenAboutToExpire', () => {

            refreshToken().catch(safeWarning);
        });

        client.on('tokenExpired', () => {

            refreshToken().catch(safeWarning);
        });

        // Connection errors: yell on fatal auth
        client.on('connectionError', (err) => {

            recordTwilioRecoveryEvent('connection_error', { error: err });

            if (isFatalTwilioAuthError(err)) {
                yellTwilioFatalAuthError(err, { phase: 'client.connectionError event' });
                scheduleReconnect({ forceRecreate: true, immediate: true });
                return;
            }

            scheduleReconnect();
        });

        client.on('connectionStateChanged', (state) => {

            recordTwilioRecoveryEvent('connection_state_changed', { state });

            if (state === 'connected') {
                lastTransportActivityAt = Date.now();
                resetReconnectState();
                return;
            }

            if (state === 'denied') {
                scheduleReconnect({ forceRecreate: true, immediate: true });
                return;
            }

            if (state === 'disconnected') {
                scheduleReconnect();
            }
        });

        twilioClientListenersAttached = true;
    };

    const detachClientCoreListeners = (client) => {

        if (!client || !twilioClientListenersAttached) {
            return;
        }

        client.removeAllListeners('tokenAboutToExpire');
        client.removeAllListeners('tokenExpired');
        client.removeAllListeners('connectionError');
        client.removeAllListeners('connectionStateChanged');

        twilioClientListenersAttached = false;
    };

    const createClientOrThrow = async ({ phase, generation, sessionToken }) => {

        assertActiveGeneration(generation, sessionToken);
        const token = getTwilioToken();

        if (!token) {
            throw new Error('No Twilio token available');
        }

        try {
            const client = await Client.create(token);

            return client;
        }
        catch (err) {
            assertActiveGeneration(generation, sessionToken);

            // One forced refresh attempt for fatal auth errors (in case state had stale token)
            if (isFatalTwilioAuthError(err) && !didForceRefreshAfterFatalAuth) {
                didForceRefreshAfterFatalAuth = true;

                yellTwilioFatalAuthError(err, { phase: `${phase}: initial create -> forcing single token refresh` });

                const refreshedToken = await fetchFreshToken();

                try {
                    const client = await Client.create(refreshedToken);

                    return client;
                }
                catch (err2) {
                    if (isFatalTwilioAuthError(err2)) {
                        yellTwilioFatalAuthError(err2, { phase: `${phase}: still fatal after refresh (STOPPING)` });
                    }

                    throw err2;
                }
            }

            if (isFatalTwilioAuthError(err)) {
                yellTwilioFatalAuthError(err, { phase: `${phase}: fatal auth (STOPPING)` });
            }

            throw err;
        }
    };

    const newCache = () => {

        const generation = clientGeneration;
        const sessionToken = getIsAuthenticated() ? selectors.getApiToken?.(redux.getState()) : null;

        const adoptClient = async (client) => {

            try {
                assertActiveGeneration(generation, sessionToken);
            }
            catch (err) {
                await client.shutdown();
                throw err;
            }

            twilioClient = client;
            if (client.connectionState === 'connected') {
                lastTransportActivityAt = Date.now();
            }

            attachClientCoreListeners(client);

            if (messageEvents.listeners) {
                attachMessageEventListeners(client);
            }

            return client;
        };

        return makeCacheWithErrors({
            getClient: async () => {

                assertActiveGeneration(generation, sessionToken);

                try {
                    const client = await createClientOrThrow({ phase: 'cache.getClient', generation, sessionToken });

                    return adoptClient(client);
                }
                catch (err) {
                    if (err instanceof TransportUnavailableError) {
                        assertActiveGeneration(generation, sessionToken);
                        await fetchFreshToken();

                        const client = await createClientOrThrow({ phase: 'cache.getClient TransportUnavailableError retry', generation, sessionToken });

                        return adoptClient(client);
                    }

                    throw err;
                }
            },

            getConversation: async ({ channelSid }) => {

                const client = await cache.getClient();

                return client.getConversationBySid(channelSid);
            }
        });
    };

    const initialize = ({ reset = false } = {}) => {

        const sessionToken = getIsAuthenticated() ? selectors.getApiToken?.(redux.getState()) : null;

        if (cache && !reset && !isShutdown && sessionToken === cacheSessionToken) {
            return false;
        }

        clientGeneration += 1;
        isShutdown = false;
        cacheSessionToken = sessionToken;
        cache = newCache();
        messageEntities = {};
        messagesPaginator_current = {};
        messagesPaginator_next = {};

        didForceRefreshAfterFatalAuth = false;

        if (twilioClient) {
            try {
                detachMessageEventListeners(twilioClient);
                detachClientCoreListeners(twilioClient);
                Promise.resolve(twilioClient.shutdown()).catch(safeWarning);
            }
            catch (err) {
                safeWarning(err);
            }
            finally {
                twilioClient = null;
            }
        }

        if (!getIsAuthenticated()) {
            if (twilioClient) {
                detachClientCoreListeners(twilioClient);
            }

            twilioClient = null;
            resetReconnectState();
        }

        return true;
    };

    const messageEntities_set = (messages) => {

        [].concat(messages).forEach((m) => {

            messageEntities[m.state.sid] = m;
        });

        return messages;
    };

    const messageEntities_get = (sid) => {

        const m = messageEntities[sid] || null;

        if (m === null) {
            safeWarning(`Haven't seen message ${sid} yet!`);
        }

        return m;
    };

    const messageEntities_del = (sid) => {

        return delete messageEntities[sid];
    };

    const messagesPaginator_hasPrevPage = async ({ channelSid }) => {

        const currentPaginator = messagesPaginator_current[channelSid];

        if (currentPaginator && IsPromise(currentPaginator)) {
            return currentPaginator;
        }

        return !!currentPaginator?.hasPrevPage;
    };

    const messagesPaginator_firstPage = ({ channelSid }) => {

        const generation = clientGeneration;
        const currentPaginator = messagesPaginator_current[channelSid];

        if (currentPaginator && IsPromise(currentPaginator)) {
            return currentPaginator;
        }

        const resultPromise = Promise.resolve().then(async () => {

            await runTwilioMessageLoadSimulation({ channelSid });

            const channel = await cache.getConversation({ channelSid });
            const paginator = await channel.getMessages(MESSAGES_PAGE_SIZE);
            assertActiveGeneration(generation);

            safeInvariant(
                messagesPaginator_current[channelSid] === resultPromise,
                'Value should not change during the fetch.'
            );

            if (messagesPaginator_current[channelSid] === resultPromise) {
                messagesPaginator_current[channelSid] = paginator;
            }

            return paginator.items;
        }).catch((error) => {

            if (messagesPaginator_current[channelSid] === resultPromise) {
                delete messagesPaginator_current[channelSid];
            }

            throw error;
        });

        messagesPaginator_current[channelSid] = resultPromise;

        return resultPromise;
    };

    const messagesPaginator_nextPage = ({ channelSid }) => {

        const generation = clientGeneration;
        const currentPaginator = messagesPaginator_current[channelSid];
        const nextPaginator = messagesPaginator_next[channelSid];

        if (!currentPaginator) {
            return messagesPaginator_firstPage({ channelSid });
        }

        if (IsPromise(currentPaginator)) {
            return currentPaginator;
        }

        if (nextPaginator) {
            safeInvariant(IsPromise(nextPaginator), 'nextPaginator should be promise or null.');
            return IsPromise(nextPaginator) ? nextPaginator : Promise.resolve([]);
        }

        if (!currentPaginator.hasPrevPage) {
            return Promise.resolve([]);
        }

        const resultPromise = Promise.resolve().then(async () => {

            const paginator = await currentPaginator.prevPage();
            assertActiveGeneration(generation);

            safeInvariant(
                messagesPaginator_next[channelSid] === resultPromise,
                'Value should not change during the fetch.'
            );

            if (messagesPaginator_next[channelSid] === resultPromise) {
                messagesPaginator_current[channelSid] = paginator;
                messagesPaginator_next[channelSid] = null;
            }

            return paginator.items;
        });

        messagesPaginator_next[channelSid] = resultPromise;

        return resultPromise;
    };

    const fetchAllConversations = async (client) => {

        let items = [];

        const getPage = async (paginator) => {

            items = items.concat(paginator.items);

            if (paginator.hasNextPage) {
                const next = await paginator.nextPage();
                return getPage(next);
            }

            return items;
        };

        const subscribedConversationsPaginator = await client.getSubscribedConversations();

        return getPage(subscribedConversationsPaginator);
    };

    const twilio = {
        shutdown: async () => {

            const client = twilioClient;
            clientGeneration += 1;
            isShutdown = true;
            twilioClient = null;

            if (client) {
                detachMessageEventListeners(client);
                detachClientCoreListeners(client);
                let timeout;

                try {
                    // Native resume waits for this teardown. A suspended socket
                    // must not keep the replacement client blocked indefinitely.
                    await Promise.race([
                        client.shutdown(),
                        new Promise((_resolve, reject) => {

                            timeout = setTimeout(() => {

                                const error = new Error('Twilio client shutdown timed out');
                                error.code = 'TWILIO_SHUTDOWN_TIMEOUT';
                                reject(error);
                            }, SHUTDOWN_TIMEOUT_MS);
                        })
                    ]);
                }
                catch (err) {
                    safeWarning(err);
                }

                finally {
                    clearTimeout(timeout);
                }
            }
        },

        updateToken: async ({ token }) => {

            if (typeof token !== 'string' || !token.trim()) {
                safeWarning('Skipping Twilio token update because the refreshed token is empty.');
                return;
            }

            const client = await cache.getClient();

            await client.updateToken(token);
        },

        getSubscribedConversations: async () => {

            const client = await cache.getClient();

            return fetchAllConversations(client);
        },

        getConversationByName: async ({ uniqueName }) => {

            const client = await cache.getClient();
            const conversation = await client.getConversationByUniqueName(uniqueName);

            return cache.getConversation({ channelSid: conversation.sid });
        },

        getConversationLastMessage: async ({ channelSid }) => {

            const conversation = await twilio.getConversation({ channelSid });
            const paginator = await conversation.getMessages(1);

            return {
                [conversation.sid]: IsEmpty(paginator.items) ? null : toDomain.message(paginator.items[0])
            };
        },

        getAllConversationsLastMessage: async () => {

            const conversations = await fetchAllConversations(await cache.getClient());

            const results = {};

            for (const conversation of conversations) {
                const paginator = await conversation.getMessages(1);
                results[conversation.sid] = IsEmpty(paginator.items) ? null : toDomain.message(paginator.items[0]);
            }

            return results;
        },

        getConversation: async ({ channelSid }) => {

            return cache.getConversation({ channelSid }, { forceRefresh: true });
        },

        getMessages_firstPage: async ({ channelSid }) => {

            const generation = clientGeneration;
            const messages = await messagesPaginator_firstPage({ channelSid });
            assertActiveGeneration(generation);

            return messageEntities_set(messages);
        },

        getMessages_nextPage: async ({ channelSid }) => {

            const generation = clientGeneration;
            const messages = await messagesPaginator_nextPage({ channelSid });
            assertActiveGeneration(generation);

            return messageEntities_set(messages);
        },

        getMessages_hasPrevPage: async ({ channelSid }) => {

            const hasPrevPage = await messagesPaginator_hasPrevPage({ channelSid });

            return { result: hasPrevPage };
        },

        markMessageAsLastRead: async ({ channelSid, messageIndex }) => {

            const conversation = await cache.getConversation({ channelSid });

            return conversation.updateLastReadMessageIndex(messageIndex);
        },

        updateMessageBody: async ({ sid, body }) => {

            const message = messageEntities_get(sid);

            if (!message) {
                throw new Error(`No message ${sid}`);
            }

            const updated = await message.updateBody(body);

            messageEntities_set(updated);

            return updated;
        },

        removeMessage: async ({ messageSid }) => {

            const message = messageEntities_get(messageSid);

            if (!message) {
                throw new Error(`No message ${messageSid}`);
            }

            const removed = await message.remove();

            messageEntities_del(removed.sid);

            return removed;
        }
    };

    const schema = {
        Member: new S.Entity('members', {}, { idAttribute: 'sid' }),
        Channel: new S.Entity('channels', {}, { idAttribute: 'sid' }),
        Message: new S.Entity('messages', {}, { idAttribute: 'sid' }),
        MessageCounts: new S.Object({}),
        ChannelUpdateDates: new S.Object({}),
        ChannelLastMessage: new S.Object({})
    };

    schema.Member.define({
        channel: schema.Channel
    });

    schema.Message.define({
        channel: schema.Channel
    });

    const isPrivateChannel = (channel) => {

        return channel
            && (isValid_privateDMChannelIdentifier(channel.uniqueName)
            || isValid_privateClassChannelIdentifier(channel.uniqueName));
    };

    const isValid_privateDMChannelIdentifier = (channelIdentifier) => {

        return channelIdentifier
            && channelIdentifier.split('-').length === 3
            && channelIdentifier.split('-')[0] === configuration.environmentPrefix
            && channelIdentifier.split('-')[1] === 'nearpeer'
            && channelIdentifier.split('-')[2].split(',').length === 2;
    };

    const isValid_privateClassChannelIdentifier = (channelIdentifier) => {

        return channelIdentifier
            && channelIdentifier.split('-').length === 4
            && channelIdentifier.split('-')[0] === configuration.environmentPrefix
            && channelIdentifier.split('-')[1] === 'nearpeer'
            && channelIdentifier.split('-')[2] === 'class'
            && channelIdentifier.split('-')[3];
    };

    const isValid_userIdentifier = (userIdentifier) => {

        return userIdentifier
            && userIdentifier.split('-').length === 3
            && userIdentifier.split('-')[0] === configuration.environmentPrefix
            && userIdentifier.split('-')[1] === 'nearpeer';
    };

    const extractUserIds_fromPrivateChannelIdentifier = (channelIdentifier) => {

        if (!isValid_privateDMChannelIdentifier(channelIdentifier)) {
            safeWarning(`Invalid DM channel type! Unique name: ${channelIdentifier}`);
            return [];
        }

        return channelIdentifier.split('-')[2].split(',').map(Unary(parseInt));
    };

    const extractClassId_fromPrivateChannelIdentifier = (channelIdentifier) => {

        if (!isValid_privateClassChannelIdentifier(channelIdentifier)) {
            safeWarning(`Invalid class channel type! Unique name: ${channelIdentifier}`);
            return null;
        }

        return parseInt(channelIdentifier.split('-')[3]);
    };

    const extractUserId_fromUserIdentifier = (userIdentifier) => {

        if (!isValid_userIdentifier(userIdentifier)) {
            safeWarning(`Invalid user identifier! Identifier: ${userIdentifier}`);
            return null;
        }

        return First([userIdentifier.split('-')[2]].map(Unary(parseInt)));
    };

    const create_privateChannelIdentifier = ({ uId1, uId2 }) => {

        return `${configuration.environmentPrefix}-nearpeer-${[uId1, uId2].sort()}`;
    };

    const toDomain = {
        conversations: (conversations) => {

            return conversations.filter(isPrivateChannel).map(toDomain.conversation);
        },

        conversation: (conversation) => {

            const type = isValid_privateDMChannelIdentifier(conversation.uniqueName) ? 'dm'
                : isValid_privateClassChannelIdentifier(conversation.uniqueName) ? 'class'
                    : null;

            if (type === null) {
                safeWarning(`Invalid channel identifier! Identifier: ${conversation.uniqueName}`);
            }

            return {
                sid: conversation.sid,
                type,
                userIds: (type === 'dm')
                    ? extractUserIds_fromPrivateChannelIdentifier(conversation.uniqueName)
                    : null,
                classId: (type === 'class')
                    ? extractClassId_fromPrivateChannelIdentifier(conversation.uniqueName)
                    : null
            };
        },

        messages: (messages) => {

            return messages.map(toDomain.message);
        },

        message: (message) => {

            return {
                sid: message.sid,
                channel: toDomain.conversation(message.conversation),
                author: extractUserId_fromUserIdentifier(message.author),
                body: message.body,
                timestamp: message.dateCreated,
                index: message.index,
                moderationStatus: message.attributes.moderationStatus || 'ok',
                isPinned: message.attributes.isPinned || false,
                isEdited: message.attributes.isEdited || false,
                reactions: message.attributes.reactions || {},
                clientMessageId: message.attributes.clientMessageId
            };
        },

        member: ({ sid, channel, identity }) => {

            return {
                sid,
                id: extractUserId_fromUserIdentifier(identity),
                conversation: toDomain.conversation(channel)
            };
        }
    };

    const messageEvents = {
        listeners: null
    };

    const isListening = () => {

        return !!messageEvents.listeners;
    };

    const messageEvents_onMessageAdded = (message) => {

        lastTransportActivityAt = Date.now();
        return messageEvents.listeners.onMessageAdded(
            normalize(toDomain.message(messageEntities_set(message)), schema.Message)
        );
    };

    const messageEvents_onMessageUpdated = ({ message }) => {

        lastTransportActivityAt = Date.now();
        return messageEvents.listeners.onMessageUpdated(
            normalize(toDomain.message(messageEntities_set(message)), schema.Message)
        );
    };

    const messageEvents_onMessageRemoved = (message) => {

        lastTransportActivityAt = Date.now();
        return messageEvents.listeners.onMessageRemoved(
            normalize(toDomain.message(messageEntities_set(message)), schema.Message)
        );
    };

    const attachMessageEventListeners = (client) => {

        if (messageListenerClient === client) {
            return;
        }

        messageListenerClient = client;
        client.on('messageAdded', messageEvents_onMessageAdded);
        client.on('messageUpdated', messageEvents_onMessageUpdated);
        client.on('messageRemoved', messageEvents_onMessageRemoved);
        client.on('conversationAdded', conversationEvents_onConversationAdded);
        client.on('conversationUpdated', conversationEvents_onConversationUpdated);
        client.on('conversationRemoved', conversationEvents_onConversationRemoved);
        client.on('conversationJoined', conversationEvents_onConversationJoined);
        client.on('conversationLeft', conversationEvents_onConversationLeft);
    };

    const detachMessageEventListeners = (client) => {

        if (messageListenerClient !== client) {
            return;
        }

        messageListenerClient = null;
        client.removeListener('messageAdded', messageEvents_onMessageAdded);
        client.removeListener('messageUpdated', messageEvents_onMessageUpdated);
        client.removeListener('messageRemoved', messageEvents_onMessageRemoved);
        client.removeListener('conversationAdded', conversationEvents_onConversationAdded);
        client.removeListener('conversationUpdated', conversationEvents_onConversationUpdated);
        client.removeListener('conversationRemoved', conversationEvents_onConversationRemoved);
        client.removeListener('conversationJoined', conversationEvents_onConversationJoined);
        client.removeListener('conversationLeft', conversationEvents_onConversationLeft);
    };

    const conversationEvents_onConversationAdded = (conversation) => {

        return messageEvents.listeners.onConversationAdded?.(
            normalize(toDomain.conversation(conversation), schema.Channel)
        );
    };

    const conversationEvents_onConversationUpdated = ({ conversation }) => {

        return messageEvents.listeners.onConversationUpdated?.(
            normalize(toDomain.conversation(conversation), schema.Channel)
        );
    };

    const conversationEvents_onConversationRemoved = (conversation) => {

        return messageEvents.listeners.onConversationRemoved?.(
            normalize(toDomain.conversation(conversation), schema.Channel)
        );
    };

    const conversationEvents_onConversationJoined = (conversation) => {

        return messageEvents.listeners.onConversationJoined?.(
            normalize(toDomain.conversation(conversation), schema.Channel)
        );
    };

    const conversationEvents_onConversationLeft = (conversation) => {

        return messageEvents.listeners.onConversationLeft?.(
            normalize(toDomain.conversation(conversation), schema.Channel)
        );
    };

    const messageEvents_startListening = async ({ listeners }) => {

        if (isListening()) {
            safeWarning('Listening already in progress!');
            return;
        }

        messageEvents.listeners = listeners;

        const client = await cache.getClient();

        if (messageEvents.listeners === listeners && client === twilioClient) {
            attachMessageEventListeners(client);
        }
    };

    const messageEvents_stopListening = async () => {

        if (!isListening()) {
            safeWarning('No listening in progress!');
            return;
        }

        if (twilioClient) {
            detachMessageEventListeners(twilioClient);
        }

        messageEvents.listeners = null;
    };

    const reconnectClient = async ({ forceRecreate = false } = {}) => {

        if (!getIsAuthenticated() || isShutdown) {
            resetReconnectState();
            return;
        }

        reconnectAttempts += 1;

        const recoveryStartedAt = Date.now();

        recordTwilioRecoveryEvent('client_recovery_started', {
            attempt: reconnectAttempts,
            forceRecreate
        });

        let generation = clientGeneration;
        const sessionToken = selectors.getApiToken?.(redux.getState());

        try {
            const refreshedToken = await fetchFreshToken();
            assertActiveGeneration(generation, sessionToken);

            if (!forceRecreate && twilioClient) {
                try {
                    await twilioClient.updateToken(refreshedToken);
                }
                catch (err) {
                    safeWarning(err);
                    forceRecreate = true;
                }
            }

            assertActiveGeneration(generation, sessionToken);

            if (forceRecreate || !twilioClient) {
                initialize({ reset: true });
                generation = clientGeneration;
                await cache.getClient();
            }

            assertActiveGeneration(generation, sessionToken);

            reconnectAttempts = 0;
            clearReconnectTimeout();

            // List/preview reads must not hold a usable transport behind their
            // own network latency or turn a pending send into a recovery timeout.
            Promise.resolve().then(async () => {

                assertActiveGeneration(generation, sessionToken);
                await redux.dispatch(actions.dataFetching.fetchChannels());
                assertActiveGeneration(generation, sessionToken);
                await redux.dispatch(actions.dataFetching.fetchAllChannelLastMessages());
            }).catch((error) => {

                if (generation === clientGeneration && !isShutdown
                    && sessionToken === selectors.getApiToken?.(redux.getState())) {
                    recordTwilioRecoveryEvent('conversation_refresh_failed', { error: {
                        code: error.code ?? error.status ?? null,
                        name: error.name,
                        message: 'Conversation list refresh failed'
                    } });
                }
            });

            recordTwilioRecoveryEvent('client_recovery_succeeded', {
                elapsedMs: Date.now() - recoveryStartedAt,
                forceRecreate
            });
        }
        catch (error) {
            recordTwilioRecoveryEvent('client_recovery_failed', {
                elapsedMs: Date.now() - recoveryStartedAt,
                error,
                forceRecreate
            });

            throw error;
        }
    };

    const beginReconnect = (options) => {

        let timeout;
        const deadline = new Promise((resolve, reject) => {

            cancelReconnect = () => {

                const error = new Error('Twilio recovery was superseded by initialization or shutdown');
                error.code = 'TWILIO_OPERATION_SUPERSEDED';
                reject(error);
            };

            timeout = setTimeout(() => {

                // Abandon this generation before freeing the retry guard so a
                // late token/client result cannot revive the timed-out attempt.
                if (reconnectPromise === promise && !isShutdown && getIsAuthenticated()) {
                    initialize({ reset: true });
                }

                const error = new Error('Twilio connection recovery timed out');
                error.code = 'TWILIO_RECOVERY_TIMEOUT';
                reject(error);
            }, RECOVERY_TIMEOUT_MS);
        });
        const promise = Promise.race([reconnectClient(options), deadline]).finally(() => {

            clearTimeout(timeout);

            if (reconnectPromise === promise) {
                reconnectPromise = null;
                cancelReconnect = null;
            }
        });
        reconnectPromise = promise;
        return promise;
    };

    const scheduleReconnect = ({ forceRecreate = false, immediate = false } = {}) => {

        if (!getIsAuthenticated() || isShutdown) {
            return;
        }

        if (reconnectPromise) {
            return;
        }

        const shouldReplaceExistingTimer = immediate || forceRecreate;

        if (reconnectTimeout && !shouldReplaceExistingTimer) {
            return;
        }

        clearReconnectTimeout();

        reconnectTimeout = setTimeout(() => {

            reconnectTimeout = null;
            beginReconnect({ forceRecreate }).catch((err) => {

                safeWarning(err);
                if (err.code !== 'TWILIO_OPERATION_SUPERSEDED') {
                    scheduleReconnect({ forceRecreate: true });
                }
            });
        }, immediate ? 0 : getReconnectDelay());
    };

    const recoverConnection = () => {

        if (reconnectPromise) {
            return reconnectPromise;
        }

        clearReconnectTimeout();
        return beginReconnect({ forceRecreate: true }).catch((error) => {

            if (error.code !== 'TWILIO_OPERATION_SUPERSEDED') {
                scheduleReconnect({ forceRecreate: true });
            }

            throw error;
        });
    };

    const waitForConnected = (client, deadline) => new Promise((resolve, reject) => {

        if (client.connectionState === 'connected') {
            resolve();
            return;
        }

        const finish = (error) => {

            clearTimeout(timeout);
            client.removeListener('connectionStateChanged', onState);
            error ? reject(error) : resolve();
        };
        const onState = (state) => {

            if (state === 'connected') {
                finish();
            }
        };
        const timeout = setTimeout(() => {

            const error = new Error('Chat connection did not become ready');
            error.code = 'TWILIO_CONNECTION_TIMEOUT';
            finish(error);
        }, Math.max(0, deadline - Date.now()));

        client.on('connectionStateChanged', onState);

        if (client.connectionState === 'connected') {
            finish();
        }
    });

    const ensureConnection = ({ force = false } = {}) => {

        if (connectionCheckPromise) {
            return connectionCheckPromise;
        }

        const sessionToken = selectors.getApiToken?.(redux.getState());
        const version = connectionCheckVersion;
        const deadline = Date.now() + RECOVERY_TIMEOUT_MS;
        let expired = false;
        let timeout;
        const assertCurrent = () => {

            assertActiveGeneration(clientGeneration, sessionToken);

            if (version !== connectionCheckVersion) {
                const error = new Error('Chat connection check belongs to an inactive session');
                error.code = 'TWILIO_OPERATION_SUPERSEDED';
                throw error;
            }

            if (expired) {
                const error = new Error('Chat connection check timed out');
                error.code = 'TWILIO_CONNECTION_TIMEOUT';
                throw error;
            }
        };
        const checking = (async () => {

            assertCurrent();

            if (reconnectPromise) {
                await reconnectPromise;
            }

            assertCurrent();
            let client = await cache.getClient();
            assertCurrent();

            if (force || client.connectionState !== 'connected' || Date.now() - lastTransportActivityAt >= IDLE_CONNECTION_MS) {
                await recoverConnection();
                assertCurrent();
                client = await cache.getClient();
            }

            assertCurrent();
            await waitForConnected(client, deadline);
            assertCurrent();
        })();
        const timingOut = new Promise((_resolve, reject) => {

            timeout = setTimeout(() => {

                expired = true;
                const error = new Error('Chat connection check timed out');
                error.code = 'TWILIO_CONNECTION_TIMEOUT';
                reject(error);
            }, RECOVERY_TIMEOUT_MS);
        });
        const promise = Promise.race([checking, timingOut]).finally(() => {

            clearTimeout(timeout);

            if (connectionCheckPromise === promise) {
                connectionCheckPromise = null;
            }
        });
        connectionCheckPromise = promise;
        return promise;
    };

    const getSendConversation = async ({ channelSid }) => {

        let timeout;

        try {
            return await Promise.race([
                cache.getConversation({ channelSid }),
                new Promise((_resolve, reject) => {

                    timeout = setTimeout(() => {

                        const error = new Error('Message conversation lookup timed out');
                        error.code = 'TWILIO_SEND_CONVERSATION_TIMEOUT';
                        reject(error);
                    }, SEND_CONVERSATION_TIMEOUT_MS);
                })
            ]);
        }
        finally {
            clearTimeout(timeout);
        }
    };

    return {
        getChannels: withActiveGeneration(async () => {

            const data = await twilio.getSubscribedConversations();

            return normalize(toDomain.conversations(data), [schema.Channel]);
        }),

        getDMChannelByUsers: withActiveGeneration(async (uId1, uId2) => {

            try {
                const data = await twilio.getConversationByName({
                    uniqueName: create_privateChannelIdentifier({
                        uId1,
                        uId2
                    })
                });

                return normalize(toDomain.conversation(data), schema.Channel);
            }
            catch (err) {
                return {
                    error: err,
                    result: null
                };
            }
        }),

        getConversationLastMessage: withActiveGeneration(async ({ channelSid }) => {

            const data = await twilio.getConversationLastMessage({ channelSid });

            return normalize(data, schema.ChannelLastMessage);
        }),

        getAllConversationsLastMessage: withActiveGeneration(async () => {

            const data = await twilio.getAllConversationsLastMessage();

            return normalize(data, schema.ChannelLastMessage);
        }),

        getConversation: withActiveGeneration(async ({ channelSid }) => {

            const data = await twilio.getConversation({ channelSid });

            return normalize(toDomain.conversation(data), schema.Channel);
        }),

        getHasPrevPage: withActiveGeneration(async ({ channelSid }) => {

            return twilio.getMessages_hasPrevPage({ channelSid });
        }),

        getMessages: withActiveGeneration(async ({ channelSid }) => {

            const data = await twilio.getMessages_firstPage({ channelSid });

            return normalize(toDomain.messages(data), [schema.Message]);
        }),

        getMoreMessages: withActiveGeneration(async ({ channelSid }) => {

            const data = await twilio.getMessages_nextPage({ channelSid });

            return normalize(toDomain.messages(data), [schema.Message]);
        }),

        sendMessage: async ({ channelSid, message, clientMessageId }) => {

            const sessionToken = selectors.getApiToken?.(redux.getState());
            const version = connectionCheckVersion;
            let started = false;
            let expired = false;
            let timeout;
            const assertCurrent = () => {

                assertActiveGeneration(clientGeneration, sessionToken);

                if (version !== connectionCheckVersion) {
                    const error = new Error('Message send belongs to an inactive session');
                    error.code = 'TWILIO_OPERATION_SUPERSEDED';
                    throw error;
                }

                if (expired) {
                    const error = new Error('Message send timed out');
                    error.code = 'TWILIO_SEND_TIMEOUT';
                    throw error;
                }
            };
            const sending = (async () => {

                for (let attempt = 0; attempt < 2; ++attempt) {
                    try {
                        await ensureConnection();
                        assertCurrent();
                        const generation = clientGeneration;
                        const conversation = await getSendConversation({ channelSid });
                        assertCurrent();
                        assertActiveGeneration(generation, sessionToken);
                        started = true;
                        let messageIndex;

                        try {
                            messageIndex = await conversation.sendMessage(message, clientMessageId ? { clientMessageId } : {});
                        }
                        catch (error) {
                            // Twilsock rejects queued/unsubmitted requests with this
                            // error. Other write failures may have lost only the ACK.
                            if (error instanceof TransportUnavailableError || error?.name === 'TransportUnavailableError') {
                                started = false;
                            }

                            throw error;
                        }

                        assertActiveGeneration(generation, sessionToken);
                        lastTransportActivityAt = Date.now();
                        return { messageIndex, clientMessageId };
                    }
                    catch (error) {
                        assertCurrent();
                        const status = error?.status ?? error?.httpStatusCode;
                        const permanent = isFatalTwilioAuthError(error)
                            || (status >= 400 && status < 500 && ![401, 408, 429].includes(status));

                        if (attempt || started || permanent) {
                            throw error;
                        }

                        recordTwilioRecoveryEvent('send_recovery_started', { channelSid, error });
                        await recoverConnection();
                        assertCurrent();
                    }
                }
            })();
            const timingOut = new Promise((_resolve, reject) => {

                timeout = setTimeout(() => {

                    expired = true;
                    const error = new Error('Message send timed out');
                    error.code = 'TWILIO_SEND_TIMEOUT';
                    reject(error);
                }, SEND_TIMEOUT_MS);
            });

            try {
                return await Promise.race([sending, timingOut]);
            }
            catch (error) {
                expired = true;
                error.deliveryUncertain = started;
                recordTwilioRecoveryEvent('send_failed', { channelSid, error: {
                    code: error.code ?? error.status ?? error.httpStatusCode ?? null,
                    name: error.name,
                    message: 'Message delivery failed'
                } });

                if (error.code !== 'TWILIO_OPERATION_SUPERSEDED'
                    && (error.code === 'TWILIO_SEND_TIMEOUT' || error.code === 'TWILIO_CONNECTION_TIMEOUT'
                        || twilioClient?.connectionState !== 'connected')) {
                    // Repair future sends and reconcile echoes. An attempted write
                    // is never replayed: its acknowledgment may simply have been lost.
                    recoverConnection().catch(safeWarning);
                }

                throw error;
            }
            finally {
                clearTimeout(timeout);
            }
        },

        updateMessageBody: async ({ sid, body }) => {

            const data = await twilio.updateMessageBody({ sid, body });

            normalize(toDomain.message(data), schema.Message);

            return { message: data };
        },

        removeMessage: async ({ messageSid }) => {

            await twilio.removeMessage({ messageSid });

            return Constant(undefined)();
        },

        markMessageAsLastRead: async ({ channelSid, messageIndex = null }) => {

            await twilio.markMessageAsLastRead({ channelSid, messageIndex });

            return Constant(undefined)();
        },

        startListeningMessages: async ({ listeners }) => {

            await messageEvents_startListening({ listeners });
        },

        stopListeningMessages: async () => {

            await messageEvents_stopListening();
        },

        shutdown: async () => {

            if (isListening()) {
                await messageEvents_stopListening();
            }

            supersedeReconnect();
            await twilio.shutdown();

            return Constant(undefined)();
        },

        initialize: async ({ reset = false } = {}) => {

            if (initialize({ reset })) {
                supersedeReconnect();

                if (isListening()) {
                    await messageEvents_stopListening();
                }
            }
        },

        destroyAccessManager: async () => {

            // AccessManager removed in Conversations version.
            // Keep the client available for the following shutdown call.
            return Constant(undefined)();
        },

        updateToken: async ({ token }) => {

            try {
                await twilio.updateToken({ token });
                resetReconnectState();
            }
            catch (err) {
                safeWarning(err);
                scheduleReconnect({ forceRecreate: true, immediate: true });
            }

            return Constant(undefined)();
        },

        recoverConnection,
        ensureConnection
    };
};

exports.MESSAGES_PAGE_SIZE = MESSAGES_PAGE_SIZE;
